import { useState } from "react";

/**
 * SVG sparkline (line or bars) with hover tooltip + summary indicator.
 * Accepts an array of `{date, value}` points.
 */
export function Sparkline({ points, stroke, wide = false, mode = "line" }) {
  const [hover, setHover] = useState(null);
  const W = wide ? 120 : 70;
  const H = 28, PAD = 2;
  const values = points.map(p => p.value);
  const min = mode === "bars" ? 0 : Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = (W - PAD * 2) / (values.length - 1 || 1);
  const coords = values.map((v, i) => {
    const x = PAD + i * step;
    const y = H - PAD - ((v - min) / range) * (H - PAD * 2);
    return [x, y];
  });
  const path = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const areaPath = `${path} L${(PAD + (values.length - 1) * step).toFixed(1)},${H - PAD} L${PAD},${H - PAD} Z`;
  const firstVal = values[0], lastVal = values[values.length - 1];
  const activeIdx = hover ?? coords.length - 1;
  const [ax, ay] = coords[activeIdx];
  const active = points[activeIdx];
  const fmtDate = (s) => {
    try {
      return new Date(s).toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" });
    } catch { return s; }
  };

  // Summary indicator
  let summary;
  if (mode === "bars") {
    const total = values.reduce((a, b) => a + b, 0);
    const totalFmt = total.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    summary = (
      <span className="text-[10px] font-medium tabular-nums mt-0.5 text-muted-foreground">
        Σ €{totalFmt}
      </span>
    );
  } else {
    const up = lastVal >= firstVal;
    const pct = firstVal > 0 ? ((lastVal - firstVal) / firstVal) * 100 : 0;
    summary = (
      <span className={`text-[10px] font-medium tabular-nums mt-0.5 ${up ? "text-emerald-700" : "text-red-700"}`}>
        {up ? "▲" : "▼"} {Math.abs(pct).toFixed(1)}%
      </span>
    );
  }

  const barSlot = (W - PAD * 2) / values.length;
  const barW = Math.max(1, barSlot * 0.65);

  return (
    <div className="flex flex-col items-end flex-shrink-0 relative" data-testid="kpi-sparkline">
      <svg
        width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="overflow-visible cursor-crosshair"
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * W;
          let idx;
          if (mode === "bars") {
            idx = Math.max(0, Math.min(values.length - 1, Math.floor((x - PAD) / barSlot)));
          } else {
            idx = Math.max(0, Math.min(values.length - 1, Math.round((x - PAD) / step)));
          }
          setHover(idx);
        }}
      >
        {mode === "bars" ? (
          <>
            {values.map((v, i) => {
              const bx = PAD + i * barSlot + (barSlot - barW) / 2;
              const h = Math.max(0, ((v - min) / range) * (H - PAD * 2));
              const by = H - PAD - h;
              const isActive = hover === i;
              return (
                <rect
                  key={i}
                  x={bx.toFixed(1)} y={by.toFixed(1)}
                  width={barW.toFixed(1)} height={Math.max(0.5, h).toFixed(1)}
                  fill={stroke}
                  opacity={v === 0 ? 0.15 : isActive ? 1 : 0.7}
                  rx="0.5"
                />
              );
            })}
          </>
        ) : (
          <>
            <path d={areaPath} fill={stroke} opacity="0.12" />
            <path d={path} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            {hover !== null && (
              <line x1={ax} y1={PAD} x2={ax} y2={H - PAD} stroke={stroke} strokeWidth="0.75" strokeDasharray="1.5,1.5" opacity="0.5" />
            )}
            <circle cx={ax} cy={ay} r="2.4" fill={stroke} />
          </>
        )}
        <rect x="0" y="0" width={W} height={H} fill="transparent" data-testid="kpi-sparkline-hit" />
      </svg>
      {summary}
      {hover !== null && active && (
        <div
          className="absolute z-10 pointer-events-none rounded-md border bg-popover text-popover-foreground shadow-md px-2 py-1 text-[10px] leading-tight whitespace-nowrap"
          style={{ right: 0, bottom: `${H + 14}px` }}
          data-testid="kpi-sparkline-tooltip"
        >
          <div className="font-medium capitalize">{fmtDate(active.date)}</div>
          <div className="font-mono tabular-nums" style={{ color: stroke }}>
            €{(active.value || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
      )}
    </div>
  );
}
